'use client'
import { useLogInMutation } from 'codegen/generated/user'
import { ChallengeType } from 'codegen/types'
import { Divider } from '../divider'
import { useTranslations } from 'next-intl'
import * as Form from 'primitives/form'
import { SpinnerIcon } from 'primitives/icons'
import { LockFasIcon } from 'primitives/icons/security'
import * as React from 'react'
import type { UserAuthResponse } from '@repo/auth-utils'
import { getDeviceId, saveToken } from '@repo/auth-utils'
import { isError } from 'utils/js'
import { isLoggedInSelector } from 'auth/selectors'

import { useUserAuthModal } from './user-auth-dialog-context'

export function LoginOtp({
  onSuccess,
  onRegister,
  onLostPassword,
}: {
  onSuccess(user: UserAuthResponse): void
  onRegister(formState: FormData): void
  onLostPassword(formState: FormData): void
}) {
  const t = useTranslations('login_otp')
  const { formState, setStep, setErrorMessage } = useUserAuthModal()

  const loginMutation = useLogInMutation({
    onSuccess: async function onSuccessCallback(data) {
      if (data.login?.token) {
        await saveToken(data.login.token, 'login').catch(() => {
          throw new Error('CF_INVALID_TOKEN')
        })
        onSuccess(data.login)
        return
      }

      if (data.login?.challenge?.type === ChallengeType.ChallengeOtp) {
        return
      }

      if (data.login?.errors?.find(err => err.message === 'otp')) {
        throw new Error('CF_INVALID_OTP')
      }

      throw new Error('UNKNOWN_LOGIN_OTP_ERROR')
    },
  })

  const ref = React.useRef<HTMLFormElement>(null)
  const [otp, setOtp] = React.useState(formState?.get('otp')?.toString() ?? '')
  const isAutoSubmit = React.useRef(false)

  function performLogin(otpValue: string) {
    const email = formState?.get('email')?.toString() ?? ''
    const password = formState?.get('password')?.toString() ?? ''
    const deviceId = getDeviceId()
    const remember = formState?.get('remember')?.toString() === 'on'

    loginMutation.mutate({
      input: {
        email,
        deviceId,
        password,
        otp: otpValue,
        remember,
      },
    })
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    isAutoSubmit.current = false
    performLogin(otp)
  }

  function handleOtpChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newValue = e.target.value
    setOtp(newValue)
    setErrorMessage(null)

    if (newValue.length === 4) {
      isAutoSubmit.current = true
      performLogin(newValue)
    }
  }

  function onResendOTP() {
    if (loginMutation.isPending) {
      return null
    }

    const email = formState?.get('email')?.toString() ?? ''
    const password = formState?.get('password')?.toString() ?? ''
    const remember = formState?.get('remember')?.toString() === 'on'
    const deviceId = getDeviceId()

    loginMutation.mutate(
      {
        input: {
          email,
          deviceId,
          password,
          remember,
        },
      },
      {
        onSettled(data) {
          if (data?.login?.challenge?.otp) {
            const element = ref.current?.querySelector('#otp')
            if (element instanceof HTMLInputElement) {
              element.value = data.login.challenge.otp.toString()
            }
          }
        },
      },
    )
  }

  function onRegisterClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault()

    onRegister(new FormData(ref.current!))
  }

  function onLostPasswordClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault()
    const form = new FormData(ref.current!)
    onLostPassword(form)
  }

  const email = formState?.get('email')?.toString()

  React.useEffect(() => {
    function resolveErrorMessage() {
      if (loginMutation.isPending || !loginMutation.isError) {
        return null
      }

      if (loginMutation.isPaused) {
        setErrorMessage(t('errors.offline'))
        return
      }

      if (isError(loginMutation.error) && loginMutation.error.message === 'CF_INVALID_OTP') {
        if (isAutoSubmit.current) {
          return
        }
        setErrorMessage(
          t.rich('errors.invalid_details', {
            resend: str => (
              <span
                onClick={onResendOTP}
                className="text-14 cursor-pointer font-bold text-[#ffffff] underline hover:text-[#ffffff]"
              >
                {str}
              </span>
            ),
          }),
        )
        return
      }

      setErrorMessage(t.rich('errors.general', { br: () => <br /> }))
    }

    resolveErrorMessage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginMutation.isError, loginMutation.isPending, loginMutation.isPaused, loginMutation.error])

  const disabled = loginMutation.isPending || isLoggedInSelector(loginMutation.data?.login)

  return (
    <div className="flex w-full flex-col">
      <h2 className="text-20 mb-4 text-center font-bold">{t('title')}</h2>
      <span className="text-14 mt-[5px] text-center">
        {t.rich('copy', {
          email: str => {
            if (email) {
              return <span className="font-bold">{email}</span>
            }

            return str
          },
        })}
      </span>
      <span
        onClick={() => {
          setStep('login')
        }}
        className="text-14 cursor-pointer text-center font-bold text-[#0a5ea8] underline hover:text-secondary-500"
      >
        {t('change_email')}
      </span>

      <Form.Root ref={ref} onSubmit={onSubmit} className="mt-[5px] flex w-full flex-col gap-4">
        <Form.Field name="otp">
          <Form.Label className="text-12 align-text-top">{t('otp')} *</Form.Label>
          <div className="relative flex w-full rounded border border-[#e0e2ea] pl-[10px]">
            <div className="pointer-events-none absolute inset-y-0 flex items-center">
              <LockFasIcon className="h-[14px] w-[14px] text-[#e3e1e9]" />
            </div>
            <Form.Control asChild>
              <input
                required
                value={otp}
                onChange={handleOtpChange}
                type="otp"
                name="otp"
                id="otp"
                className="text-16 lg:text-14 h-[38px] w-full p-3 pl-7 outline-none"
                autoFocus
                disabled={disabled}
              />
            </Form.Control>
          </div>
        </Form.Field>

        <Form.Submit asChild>
          <button
            disabled={disabled}
            className="text-14 mt-2 flex h-[45px] w-full items-center justify-center rounded bg-secondary-500 font-bold uppercase text-[#ffffff] transition-[background-color] hover:bg-secondary-700"
          >
            {disabled ? <SpinnerIcon className="h-[24px] w-[24px] animate-spin" /> : t('cta')}
          </button>
        </Form.Submit>
      </Form.Root>
      <span
        onClick={onLostPasswordClick}
        className="text-14 m-auto mt-4 cursor-pointer text-[#0a5ea8] underline hover:text-secondary-500"
      >
        {t('forgot_password')}
      </span>
      <Divider className="mt-2 bg-[#DFDDE5]" />
      <div className="text-14 m-auto mt-4">
        {t.rich('resend_otp', {
          resendOtp: str => (
            <span
              onClick={onResendOTP}
              className="text-14 cursor-pointer font-bold text-[#0a5ea8] underline hover:text-secondary-500"
            >
              {str}
            </span>
          ),
        })}
      </div>
      <div className="text-14 m-auto mt-2">
        {t.rich('register', {
          register: str => (
            <span
              onClick={onRegisterClick}
              className="text-14 cursor-pointer font-bold text-[#0a5ea8] underline hover:text-secondary-500"
            >
              {str}
            </span>
          ),
        })}
      </div>
    </div>
  )
}
